import {memo, useState, useEffect} from "react";
import {Link, useLocation} from "react-router-dom";
import {HiOutlineChartBar, HiOutlineUsers} from "react-icons/hi2";
import {HiOutlineSpeakerphone, HiChevronDown, HiChevronLeft, HiChevronRight, HiOutlineUserGroup, HiOutlineBookOpen, HiOutlineCode} from "react-icons/hi";
import {TbClick} from "react-icons/tb";
import {MdOutlineIntegrationInstructions} from "react-icons/md";
import {CiSettings} from "react-icons/ci";
import {SlPresent} from "react-icons/sl";
import {useSelector} from "react-redux";
import {LockClosedIcon} from "@heroicons/react/solid";
import {AiOutlineShoppingCart} from "react-icons/ai";
import {HiOutlineClipboardList} from "react-icons/hi";
import {useIsAdmin} from "../../hooks/useIsAdmin";
import useHiddenAction from "../../hooks/useHiddenAction";
import "./Leftbar.css";

const Leftbar = () => {
    const location = useLocation();
    const [expandedItems, setExpandedItems] = useState({});
    const [collapsed, setCollapsed] = useState(false);
    // isDarkMode state removed as it's not being used
    const [hoveredItem, setHoveredItem] = useState(null);
    const currentOrganisation = useSelector((state) => state.organisation?.currentOrganisation);
    const currentUserIsAdmin = useIsAdmin();
    // Hidden dev mode: press Shift+D 3 times to reveal the API Reference menu item
    const { isUnlocked: devModeUnlocked } = useHiddenAction({ key: 'd', presses: 3, requireShift: true });

    // Determine the active item based on current location
    const getActiveItem = () => {
        const path = location.pathname;
        if (path.includes('/campaigns')) return 'campaigns';
        if (path.includes('/members')) return 'members';
        if (path.includes('/analytics')) return 'analytics';
        if (path.includes('/surveys')) return 'surveys';
        if (path.includes('/item-catalog')) return 'item_catalog';
        if (path.includes('/integrations')) return 'integrations';
        if (path.includes('/events')) return 'events';
        if (path.includes('/settings')) return 'settings';
        if (path.includes('/users')) return 'users';
        if (path.includes('/user-guide')) return 'user_guide';
        if (path.includes('/api-docs')) return 'api_docs';
        return '';
    }

    const selectedItem = getActiveItem();

    // Dark mode state and effects removed as they're not being used

    // Load collapsed state from localStorage
    useEffect(() => {
        const savedCollapsed = localStorage.getItem('sidebarCollapsed');
        if (savedCollapsed !== null) {
            setCollapsed(savedCollapsed === 'true');
        }
    }, []);

    // Save collapsed state to localStorage when changed
    useEffect(() => {
        localStorage.setItem('sidebarCollapsed', collapsed);
    }, [collapsed]);

    const toggleExpand = (item) => {
        if (collapsed) return;
        
        setExpandedItems((prev) => ({
            ...prev,
            [item]: !prev[item]
        }));
    };

    const toggleCollapse = () => {
        setCollapsed(!collapsed);
    };

    const handleMouseEnter = (item) => {
        setHoveredItem(item);
    };

    const handleMouseLeave = () => {
        setHoveredItem(null);
    };

    return (
        <div
            className={`leftbar ${collapsed ? 'leftbar-collapsed' : 'leftbar-expanded'} transition-all duration-300`}
            style={{
                backgroundColor: 'var(--component-bg)',
                borderRight: '1px solid var(--border-color)'
            }}
        >
            <div className="flex flex-col h-full overflow-y-auto py-6 px-3">
                {/* Main Menu Items */}
                <div className="flex flex-col flex-grow space-y-2">
                    {/* Campaigns */}
                    <Link
                        to="/campaigns"
                        className={`menu-item ${selectedItem === "campaigns" ? 'active' : ''}`}
                        onMouseEnter={() => handleMouseEnter('campaigns')}
                        onMouseLeave={handleMouseLeave}
                        data-help-title="Campaigns"
                        data-help="Opens the Campaigns page, where you define the rules that turn customer activity into loyalty points - when a member earns points, how many they earn, and how to keep things fair."
                    >
                        <div className="flex items-center p-2.5">
                            <div className="flex items-center justify-center w-9 h-9 rounded-md menu-icon">
                                <HiOutlineSpeakerphone className="text-xl" />
                            </div>
                            
                            <span className={`menu-text ml-3 font-medium text-sm ${collapsed ? 'hidden' : ''}`}>
                                Campaigns
                            </span>
                        </div>
                        
                        {collapsed && hoveredItem === 'campaigns' && (
                            <div className="tooltip">Campaigns</div>
                        )}
                    </Link>

                    {/* Members */}
                    <div 
                        className={`menu-item ${selectedItem === "members" ? 'active' : ''}`}
                        onMouseEnter={() => handleMouseEnter('members')}
                        onMouseLeave={handleMouseLeave}
                    >
                        <button
                            onClick={() => toggleExpand("members")}
                            className="w-full p-2.5 flex items-center justify-between"
                            data-help-title="Members menu"
                            data-help="Expands the Members section with links to the Members List and Groups pages. Members are the customers participating in your loyalty program."
                        >
                            <div className="flex items-center">
                                <div className="flex items-center justify-center w-9 h-9 rounded-md menu-icon">
                                    <HiOutlineUsers className="text-xl" />
                                </div>
                                
                                <span className={`menu-text ml-3 font-medium text-sm ${collapsed ? 'hidden' : ''}`}>
                                    Members
                                </span>
                            </div>

                            {!collapsed && (
                                <HiChevronDown
                                    className={`text-lg transition-transform duration-300 menu-text ${
                                        expandedItems["members"] ? "rotate-[-180deg]" : "rotate-0"
                                    }`}
                                    style={{ color: 'var(--text-secondary)' }}
                                />
                            )}
                        </button>
                        
                        {collapsed && hoveredItem === 'members' && (
                            <div className="tooltip">Members</div>
                        )}
                    </div>

                    {/* Members Submenu */}
                    {(expandedItems["members"] || (collapsed && hoveredItem === 'members')) && (
                        <div className={`submenu ${collapsed ? 'absolute left-16 p-2 rounded-md shadow-card z-10 w-36' : ''}`}
                            style={collapsed ? {
                                backgroundColor: 'var(--component-bg)',
                                border: '1px solid var(--border-color)'
                            } : {}}
                        >
                            <Link
                                to="/members/list"
                                className={`submenu-item p-2 block ${location.pathname === "/members/list" ? 'active' : ''}`}
                                data-help-title="Members List"
                                data-help="Opens the list of everyone in your loyalty program, where you can search members and review their points, transactions, and redemptions."
                            >
                                <span className="text-sm font-medium">Members List</span>
                            </Link>
                            <Link
                                to="/members/groups"
                                className={`submenu-item p-2 block ${location.pathname === "/members/groups" ? 'active' : ''}`}
                                data-help-title="Member groups"
                                data-help="Opens the Groups page, where you organise members into segments and can share a portion of each member's earned points with the group."
                            >
                                <span className="text-sm font-medium">Groups</span>
                            </Link>
                        </div>
                    )}

                    {/* Integrations */}
                    <Link
                        to="/integrations"
                        className={`menu-item ${selectedItem === "integrations" ? 'active' : ''}`}
                        onMouseEnter={() => handleMouseEnter('integrations')}
                        onMouseLeave={handleMouseLeave}
                        data-help-title="Integrations"
                        data-help="Opens the Integrations page, where you connect LoyalBase to your other systems such as point-of-sale, e-commerce, ERP, or CRM, and monitor every submission they process."
                    >
                        <div className="flex items-center p-2.5">
                            <div className="flex items-center justify-center w-9 h-9 rounded-md menu-icon">
                                <MdOutlineIntegrationInstructions className="text-xl" />
                            </div>
                            
                            <span className={`menu-text ml-3 font-medium text-sm ${collapsed ? 'hidden' : ''}`}>
                                Integrations
                            </span>
                        </div>
                        
                        {collapsed && hoveredItem === 'integrations' && (
                            <div className="tooltip">Integrations</div>
                        )}
                    </Link>

                    {/* Loyalty Store */}
                    <div
                        className={`menu-item ${currentOrganisation?.settings?.shop?.isVerified ? '' : 'locked-item'}`}
                        onMouseEnter={() => handleMouseEnter('loyalty_store')}
                        onMouseLeave={handleMouseLeave}
                        data-help-title="Loyalty Store"
                        data-help="Opens your organisation's online loyalty store in a new tab, where members redeem their points. Stays locked until your shop domain is verified."
                    >
                        {currentOrganisation?.settings?.shop?.isVerified ? (
                            <a
                                href={currentOrganisation?.settings?.shop?.customShopDomain}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-full p-2.5 flex items-center"
                            >
                                <div className="flex items-center justify-center w-9 h-9 rounded-xl menu-icon">
                                    <SlPresent className="text-xl" />
                                </div>
                                
                                <span className={`menu-text ml-3 font-medium text-sm ${collapsed ? 'hidden' : ''}`}>
                                    Loyalty Store
                                </span>
                            </a>
                        ) : (
                            <div className="disabled-item w-full p-2.5 flex items-center cursor-not-allowed">
                                <div className="flex items-center justify-center w-9 h-9 rounded-xl">
                                    <SlPresent className="text-xl" />
                                </div>
                                
                                <span className={`menu-text ml-3 font-medium text-sm ${collapsed ? 'hidden' : ''}`}>
                                    Loyalty Store
                                </span>
                                
                                {!collapsed && (
                                    <LockClosedIcon className="locked-icon ml-2 w-4 h-4" style={{ color: 'var(--text-secondary)' }} />
                                )}
                            </div>
                        )}
                        
                        {collapsed && hoveredItem === 'loyalty_store' && (
                            <div className="tooltip">
                                Loyalty Store 
                                {!currentOrganisation?.settings?.shop?.isVerified && ' (Locked)'}
                            </div>
                        )}
                    </div>

                    {/* Analytics */}
                    <Link
                        to="/analytics"
                        className={`menu-item ${selectedItem === "analytics" ? 'active' : ''}`}
                        onMouseEnter={() => handleMouseEnter('analytics')}
                        onMouseLeave={handleMouseLeave}
                        data-help-title="Analytics"
                        data-help="Opens the Analytics dashboard showing how your loyalty program is performing - how many members you have, how active they are, which campaigns are working, and how much revenue flows through the program."
                    >
                        <div className="flex items-center p-2.5">
                            <div className="flex items-center justify-center w-9 h-9 rounded-md menu-icon">
                                <HiOutlineChartBar className="text-xl" />
                            </div>
                            
                            <span className={`menu-text ml-3 font-medium text-sm ${collapsed ? 'hidden' : ''}`}>
                                Analytics
                            </span>
                        </div>
                        
                        {collapsed && hoveredItem === 'analytics' && (
                            <div className="tooltip">Analytics</div>
                        )}
                    </Link>

                    {/* Surveys */}
                    <Link
                        to="/surveys"
                        className={`menu-item ${selectedItem === "surveys" ? 'active' : ''}`}
                        onMouseEnter={() => handleMouseEnter('surveys')}
                        onMouseLeave={handleMouseLeave}
                        data-help-title="Surveys"
                        data-help="Opens the Surveys page, where you collect feedback or information from your members and reward them for completing it. You design the survey, share a link, and watch responses come in."
                    >
                        <div className="flex items-center p-2.5">
                            <div className="flex items-center justify-center w-9 h-9 rounded-md menu-icon">
                                <HiOutlineClipboardList className="text-xl" />
                            </div>
                            
                            <span className={`menu-text ml-3 font-medium text-sm ${collapsed ? 'hidden' : ''}`}>
                                Surveys
                            </span>
                        </div>
                        
                        {collapsed && hoveredItem === 'surveys' && (
                            <div className="tooltip">Surveys</div>
                        )}
                    </Link>

                    {/* Item Catalog */}
                    <Link
                        to="/item-catalog"
                        className={`menu-item ${selectedItem === "item_catalog" ? 'active' : ''}`}
                        onMouseEnter={() => handleMouseEnter('item_catalog')}
                        onMouseLeave={handleMouseLeave}
                        data-help-title="Item Catalog"
                        data-help="Opens the Item Catalog, the master list of products or services you sell. LoyalBase uses it to match incoming activity to known items for category-based campaigns, reward redemption, and reporting."
                    >
                        <div className="flex items-center p-2.5">
                            <div className="flex items-center justify-center w-9 h-9 rounded-md menu-icon">
                                <AiOutlineShoppingCart className="text-xl" />
                            </div>
                            
                            <span className={`menu-text ml-3 font-medium text-sm ${collapsed ? 'hidden' : ''}`}>
                                Item Catalog
                            </span>
                        </div>
                        
                        {collapsed && hoveredItem === 'item_catalog' && (
                            <div className="tooltip">Item Catalog</div>
                        )}
                    </Link>

                    {/* Events (Disabled) */}
                    <div 
                        className="menu-item disabled-item"
                        onMouseEnter={() => handleMouseEnter('events')}
                        onMouseLeave={handleMouseLeave}
                        data-help-title="Events"
                        data-help="Reserved for the upcoming Events feature. It is not available yet."
                    >
                        <div className="flex items-center p-2.5">
                            <div className="flex items-center justify-center w-9 h-9 rounded-xl">
                                <TbClick className="text-xl" />
                            </div>
                            
                            <span className={`menu-text ml-3 font-medium text-sm ${collapsed ? 'hidden' : ''}`}>
                                Events
                            </span>
                            
                            {!collapsed && (
                                <span className="ml-2 text-xs px-1.5 py-0.5 rounded-sm bg-signature-cream text-ink">
                                    Soon
                                </span>
                            )}
                        </div>
                        
                        {collapsed && hoveredItem === 'events' && (
                            <div className="tooltip">Events (Coming Soon)</div>
                        )}
                    </div>
                </div>

                <div className="divider"></div>

                {/* Users — admin only */}
                {currentUserIsAdmin && (
                    <Link
                        to="/users"
                        className={`menu-item ${selectedItem === "users" ? 'active' : ''}`}
                        onMouseEnter={() => handleMouseEnter('users')}
                        onMouseLeave={handleMouseLeave}
                        data-help-title="Users"
                        data-help="Opens the Users page, where you manage who from your team can access the dashboard - invite colleagues, assign their role, and remove access when someone leaves. Visible to admins only."
                    >
                        <div className="flex items-center p-2.5">
                            <div className="flex items-center justify-center w-9 h-9 rounded-md menu-icon">
                                <HiOutlineUserGroup className="text-xl" />
                            </div>

                            <span className={`menu-text ml-3 font-medium text-sm ${collapsed ? 'hidden' : ''}`}>
                                Users
                            </span>
                        </div>

                        {collapsed && hoveredItem === 'users' && (
                            <div className="tooltip">Users</div>
                        )}
                    </Link>
                )}

                {/* User Guide */}
                <Link
                    to="/user-guide"
                    className={`menu-item ${selectedItem === "user_guide" ? 'active' : ''}`}
                    onMouseEnter={() => handleMouseEnter('user_guide')}
                    onMouseLeave={handleMouseLeave}
                    data-help-title="User Guide"
                    data-help="Opens the in-app User Guide, with documentation explaining how to use each part of LoyalBase."
                >
                    <div className="flex items-center p-2.5">
                        <div className="flex items-center justify-center w-9 h-9 rounded-md menu-icon">
                            <HiOutlineBookOpen className="text-xl" />
                        </div>

                        <span className={`menu-text ml-3 font-medium text-sm ${collapsed ? 'hidden' : ''}`}>
                            User Guide
                        </span>
                    </div>

                    {collapsed && hoveredItem === 'user_guide' && (
                        <div className="tooltip">User Guide</div>
                    )}
                </Link>

                {/* API Reference — hidden until dev mode is unlocked (Shift+D x3) */}
                {(devModeUnlocked || selectedItem === "api_docs") && (
                <Link
                    to="/api-docs"
                    className={`menu-item ${selectedItem === "api_docs" ? 'active' : ''}`}
                    onMouseEnter={() => handleMouseEnter('api_docs')}
                    onMouseLeave={handleMouseLeave}
                    data-help-title="API Reference"
                    data-help="Opens the developer documentation for the LoyalBase API."
                >
                    <div className="flex items-center p-2.5">
                        <div className="flex items-center justify-center w-9 h-9 rounded-md menu-icon">
                            <HiOutlineCode className="text-xl" />
                        </div>

                        <span className={`menu-text ml-3 font-medium text-sm ${collapsed ? 'hidden' : ''}`}>
                            API Reference
                        </span>
                    </div>

                    {collapsed && hoveredItem === 'api_docs' && (
                        <div className="tooltip">API Reference</div>
                    )}
                </Link>
                )}

                {/* Settings */}
                <Link
                    to="/settings"
                    className={`menu-item ${selectedItem === "settings" ? 'active' : ''}`}
                    onMouseEnter={() => handleMouseEnter('settings')}
                    onMouseLeave={handleMouseLeave}
                    data-help-title="Settings"
                    data-help="Opens Settings, where you find the technical details for connecting LoyalBase to your own systems - your Organisation ID, your API key, and the custom domain for your loyalty store."
                >
                    <div className="flex items-center p-2.5">
                        <div className="flex items-center justify-center w-9 h-9 rounded-md menu-icon">
                            <CiSettings className="text-xl" />
                        </div>

                        <span className={`menu-text ml-3 font-medium text-sm ${collapsed ? 'hidden' : ''}`}>
                            Settings
                        </span>
                    </div>

                    {collapsed && hoveredItem === 'settings' && (
                        <div className="tooltip">Settings</div>
                    )}
                </Link>

                {/* Collapse/Expand Button */}
                <div className="toggle-collapse" onClick={toggleCollapse} data-help-title="Collapse sidebar" data-help="Collapses the sidebar to icons only or expands it back to full width. Your choice is remembered. When collapsed, hovering an icon shows its label.">
                    {collapsed ? <HiChevronRight /> : <HiChevronLeft />}
                </div>
            </div>
        </div>
    );
};

export default memo(Leftbar);